import { forwardRef, useContext, useEffect, useImperativeHandle, useState } from 'react';
import { useAppSelector } from '../../../../redux/reduxHooks';
import { SI_PROMO_CONFIG_KEYS, isSIPromoEnabled } from '../../../../constants/promotionKeys';
import { useNavigate } from 'react-router';
import Banner from '../../../../components/common/Banner/Banner';
import MeritoButton from '../../../../components/common/MeritoButtons/MeritoButtons';
import AddSales from './AddChannels/AddChannels';
import SalesMarketingCardContainer from './CardContainer';
import styles from './SalesMarketing.module.scss'
import { RiAddFill } from 'react-icons/ri'
import { formActionTypes, SalesMarketingSectionTypes } from '../../../../interfaces/interfaces';
import { MonthlyReportFormContext } from '../../context/MonthlyReportFormContext';
import { getMonthYearFromStr } from '../../MonthlyReportHome/MonthlyReportHome';
import { BsFillExclamationTriangleFill } from 'react-icons/bs';
import RequestChange from './RequestChange/RequestChange';

const SalesMarketing = forwardRef((props, ref) => {
  const navigate = useNavigate();
  const siPromoConfig = useAppSelector((state) => state.config.siPromoConfig);
  const { formData, setFormData, extraFormDetails } = useContext(MonthlyReportFormContext);
  // useEffect(() => {
  //   console.log('formData', formData)
  // }, [formData])

  console.log('formData', formData);

  const [data, setData] = useState(formData?.salesAndAdSpends);
  const [isAddChannel, setIsAddChannelOpen] = useState(false);
  const [sectionType, setSectionType] = useState(SalesMarketingSectionTypes.sales);
  const [selectedCard, setSelectedCard] = useState(null);
  const [actionType, setActionType] = useState(formActionTypes.add);
  const [isRequestChange, setIsRequestChange] = useState(false);
  const [requestedChangeCard, setRequestedChangeCard] = useState(null);

  useImperativeHandle(ref, () => ({
    submitForm: () => {

      return {
        success: true,
        values: {
          ...formData,
          salesAndAdSpends: data
        },
        pristine: JSON.stringify(formData?.salesAndAdSpends) === JSON.stringify(data)
      };
    }
  }), [data]);

  // 🔁 Sync with context
  // useEffect(() => {
  //   setFormData(prev => ({
  //     ...prev,
  //     salesAndAdSpends: data
  //   }));
  // }, [data]);

  // ✅ Smart update
  const updateItemSmart = (sectionType, updatedItem) => {
    setData(prevData => {
      const newData = { ...prevData };

      if (sectionType === SalesMarketingSectionTypes.sales) {
        // Update sales item
        newData[SalesMarketingSectionTypes.sales] = newData[SalesMarketingSectionTypes.sales].map(item =>
          item.uuid === updatedItem.uuid ? { ...item, ...updatedItem } : item
        );

        // Manage linked adSpend
        if (updatedItem.adSpend !== undefined) {
          const linkedAdIndex = newData[SalesMarketingSectionTypes.ads].findIndex(
            item => item.linkedSales === updatedItem.uuid
          );

          const adSpendUpdate = {
            uuid: `linked-${updatedItem.uuid}`,
            label: `${updatedItem.label} Ads`,
            currMonthValue: updatedItem.adSpend,
            linkedSales: updatedItem.uuid,
            isDeleted: updatedItem.isDeleted || false,
            deleteReason: updatedItem.deleteReason || ''
          };

          if (linkedAdIndex !== -1) {
            newData[SalesMarketingSectionTypes.ads][linkedAdIndex] = {
              ...newData[SalesMarketingSectionTypes.ads][linkedAdIndex],
              ...adSpendUpdate
            };
          } else {
            newData[SalesMarketingSectionTypes.ads] = [
              ...newData[SalesMarketingSectionTypes.ads],
              adSpendUpdate
            ];
          }
        }
      }

      if (sectionType === SalesMarketingSectionTypes.ads) {
        newData[SalesMarketingSectionTypes.ads] = newData[SalesMarketingSectionTypes.ads].map(item =>
          item.uuid === updatedItem.uuid ? { ...item, ...updatedItem } : item
        );
      }

      return newData;
    });
  };

  // ✅ Smart delete
  const deleteItemSmart = (sectionType, uuidToDelete) => {
    setData(prevData => {
      const newData = { ...prevData };

      if (sectionType === SalesMarketingSectionTypes.sales) {
        newData[SalesMarketingSectionTypes.sales] = newData[SalesMarketingSectionTypes.sales].filter(
          item => item.uuid !== uuidToDelete
        );
        newData[SalesMarketingSectionTypes.ads] = newData[SalesMarketingSectionTypes.ads].filter(
          item => !(item.linkedSales === uuidToDelete && item.uuid?.includes("linked"))
        );
      }

      if (sectionType === SalesMarketingSectionTypes.ads) {
        newData[SalesMarketingSectionTypes.ads] = newData[SalesMarketingSectionTypes.ads].filter(
          item => item.uuid !== uuidToDelete
        );
      }

      return newData;
    });
  };

  // ✅ Handle change (add/edit/delete)
  const handleChange = (obj, section, action) => {
    if (action === formActionTypes.add) {
      setData(prev => ({
        ...prev,
        [section]: [...prev[section], obj]
      }));
    } else if (action === formActionTypes.edit) {
      updateItemSmart(section, obj);
    } else if (action === formActionTypes.delete) {
      deleteItemSmart(section, obj.uuid);
    }
  };

  const sections = [
    {
      title: SalesMarketingSectionTypes.sales,
      subHeading: "Add sales from channels that are not connected to Merito.",
      dataKey: SalesMarketingSectionTypes.sales,
      info: [
        <p>Enter <b>Net Sales</b> after cancellations and discounts.</p>,
        <p>For <b>B2B</b>, enter the invoice value billed to the marketplace, not customer sales.</p>,
        <p><b>Update</b> this month's values for all sales or ads channels.</p>,
        <p><BsFillExclamationTriangleFill />Values from connected channels are auto-filled. You can <b>request a change.</b></p>
      ],
      buttonText: "Add Sales Channel"
    },
    {
      title: SalesMarketingSectionTypes.ads,
      subHeading: "Add ad spends from channels that are not connected to Merito.",
      dataKey: SalesMarketingSectionTypes.ads,
      info: [
        <p><BsFillExclamationTriangleFill />Values from connected channels are auto-filled. You can <b>request a change.</b></p>
      ],
      buttonText: "Add Ads Channel"
    }
  ]

  const updateCard = (card, section, requestType: 'Add' | 'Request' = 'Add') => {
    if (requestType == 'Request') {
      setRequestedChangeCard(card);
      setIsRequestChange(true)
    } else {
      setActionType(formActionTypes.edit)
      if (section.title == SalesMarketingSectionTypes.sales) {
        const uuid = card.uuid
        const adsObj = data[SalesMarketingSectionTypes.ads].find((opt) => opt.linkedSales === uuid);
        setSelectedCard({
          ...card,
          adSpend: adsObj?.currMonthValue ?? 0,
          sales: card.currMonthValue
        })
      } else {
        setSelectedCard({
          ...card,
          adSpend: card.currMonthValue
        })
      }
      setSectionType(section.title)
      setIsAddChannelOpen(true)
    }
  }

  const addCard = (section) => {
    setActionType(formActionTypes.add)
    setSectionType(section.title)
    setSelectedCard(null)
    setIsAddChannelOpen(true)
  }

  return (
    <>
      {isSIPromoEnabled(siPromoConfig, SI_PROMO_CONFIG_KEYS.BANNER_SALES_MARKETING) && (
        <Banner
          id="banner_si_perf_marketing"
          variant="purple"
          highlightText="Growth Needs More Than Great Marketing. "
          message="Explore Supplier Intelligence for supplier insights that improve liquidity."
          action={{ label: 'View', onClick: () => navigate('/supplier-intelligence') }}
        />
      )}
      <div className={styles.sectionRootContainer}>
        <div className={styles.sectionWrapper}>
          {
            sections.map(section => {
              return (
                <>
                  <div className={styles.sectionContainer}>
                    <div className={styles.sectionHeadingContainer}>
                      <div className={styles.sectionHeading}>
                        {section.title}
                        {/* {`(${getMonthYearFromStr(extraFormDetails?.monthYear)})`} */}
                      </div>
                    </div>
                    <div className={styles.subHeading}>
                      <p>{section.subHeading}</p>
                    </div>
                    {
                      section.info ?
                        <div className={styles.infoBanner}>
                          {
                            section.info.map((p) => {
                              return (p)
                            })
                          }
                        </div> : null
                    }
                    <div className={styles.cardContainer}>
                      {
                        data[section?.dataKey].map(card => {
                          if (card.isDeleted) return null; // Skip deleted cards
                          return (
                            <div className={styles.cardBorderContainer}>
                              <SalesMarketingCardContainer card={card}
                                updateCard={(selectedCard, requestType: 'Add' | 'Request' = 'Add') => updateCard(selectedCard, section, requestType)} />
                            </div>
                          )
                        })
                      }
                    </div>
                    <div className={styles.addButton}>
                      <MeritoButton type={'secondary-1'} size='md2' icon={<RiAddFill />} btnText={section.buttonText} onClick={() => {
                        addCard(section)
                      }} />
                    </div>
                  </div>
                </>
              )
            })
          }
        </div>
      </div>
      <AddSales
        isOpen={isAddChannel}
        setIsOpen={() => { setIsAddChannelOpen(false) }}
        actionType={actionType}
        sectionType={sectionType}
        salesOptions={data ? data[SalesMarketingSectionTypes.sales] : []}
        title={`${actionType == formActionTypes.edit ? 'Update' : "Add"} ${sectionType == SalesMarketingSectionTypes.sales ? 'Sales' : 'Ads'} Channel`}
        handleChange={handleChange}
        initialValues={selectedCard}
        monthYear={getMonthYearFromStr(extraFormDetails?.monthYear)}
      />

      <RequestChange
        isOpen={isRequestChange}
        setIsOpen={() => { setIsRequestChange(false) }}
        initialValues={requestedChangeCard}
        sectionType={sectionType}
        handleChange={handleChange}
      />
    </>
  )
}
)
export default SalesMarketing;